import { z } from 'zod';
import { AccountMarket, AccountMarkets } from '../account';
import {
  RetailerSparkFilters,
  retailerSparkFiltersSchema,
  vendorSparkFiltersSchema,
  VendorSparkFilters,
  SparkProductFilters,
  sparkProductFiltersSchema,
} from '../spark/spark.core';
import { UIEventSchedule } from '../recurring-events';

export * from './event.utils';

export const EVENT_REQUEST_STATUSES = ['approved', 'declined', 'pending'] as const;
export const EventRequestStatusEnum = z.enum(EVENT_REQUEST_STATUSES);
export const EVENT_REQUEST_STATUSES_TABLE = ['approved', 'pending', 'expired', 'declined'] as const;
const EventRequestStatusTableEnum = z.enum(EVENT_REQUEST_STATUSES_TABLE);
export const EVENT_REQUEST_SCHEDULED_STATUSES = ['active', 'upcoming', 'complete'] as const;
const EventRequestScheduledStatusEnum = z.enum(EVENT_REQUEST_SCHEDULED_STATUSES);
export const EVENT_TYPES = ['promo', 'training', 'popup', 'other'] as const;
export const EventTypeEnum = z.enum(EVENT_TYPES);
export const EVENT_COUNT_VARIANTS = ['all', 'inbox'] as const;
const EventCountVariantEnum = z.enum(EVENT_COUNT_VARIANTS);
export const EVENT_RETAILER_HOST_TYPES = ['partner', 'internal'] as const;
export const EventRetailerHostTypeEnum = z.enum(EVENT_RETAILER_HOST_TYPES);
export const EVENT_CREATOR_TYPES = ['retailer', 'vendor'] as const;
const EventCreatorTypeEnum = z.enum(EVENT_CREATOR_TYPES);
export type EventRequestStatus = z.infer<typeof EventRequestStatusEnum>;
export type EventRequestStatusTable = z.infer<typeof EventRequestStatusTableEnum>;
export type EventRequestScheduledStatus = z.infer<typeof EventRequestScheduledStatusEnum>;
export type EventType = z.infer<typeof EventTypeEnum>;
export type EventCountVariant = z.infer<typeof EventCountVariantEnum>;
export type EventRetailerHostType = z.infer<typeof EventRetailerHostTypeEnum>;
export type EventCreatorType = z.infer<typeof EventCreatorTypeEnum>;

export const EventDeclineReasons = [
  'Schedule conflict',
  'Space unavailability',
  'Insufficient staffing',
  'Misalightment with store goals',
  'Low relevance to target customers',
  'Product/Brand not carried',
  'Insufficient lead time',
  'Budget or resource constraints',
  'Other',
] as const;

export type EventDeclineReason = typeof EventDeclineReasons[number];

export const eventDeclineOptions = EventDeclineReasons.map((reason) => ({
  label: reason,
  value: reason,
}));

export type EventDeclineResponse = {
  reason?: EventDeclineReason;
  message?: string;
};

export interface UIEvent {
  _id: string;
  startTime: string;
  endTime: string;
  totalRetailerLocationCount: Number;
  eventCreatorType: EventCreatorType;
  vendorAccount?: {
    _id: string;
    name: string;
  };
  retailerAccount: {
    _id: string;
    name: string;
    photo: string;
  };
  brand?: {
    _id: string;
    name: string;
    photo: string;
  };
  locations: {
    _id: string;
    name: string;
    timeZone: string;
  }[];
  retailerHostType?: EventRetailerHostType;
  requestStatus?: EventRequestStatus;
  type: EventType;
  isExpired?: boolean;
  name: string;
  description?: string;
  requestNote?: string;
  createdAt: Date;
  updatedAt: Date;
  quickApprovalCode?: string;
  createdByUser?: {
    _id: string;
    name: string;
  };
  clonedFromId?: string;
  markets?: AccountMarket[];

  retailerFilters?: RetailerSparkFilters;
  vendorFilters?: VendorSparkFilters;
  posBrandIds?: string[];
  posCategoryIds?: string[];
  posProductIds?: string[];
  productFilters?: SparkProductFilters;
  scheduleId?: string;
  schedule?: UIEventSchedule;
  isAllDayEvent?: boolean;
  retailerTimeZone?: string;
}

type SortableKeys<T> = {
  [K in keyof T]: T[K] extends string | number | Date ? K : never;
}[keyof T];

export const SORTABLE_EVENT_FIELDS = ['createdAt', 'startTime', 'endTime', 'name'] as const;
const SortableEventFieldsEnum = z.enum(SORTABLE_EVENT_FIELDS);
export type SortableEventFields = typeof SORTABLE_EVENT_FIELDS[number] & SortableKeys<UIEvent>;

export const listEventsQueryParamsSchema = z.object({
  accountId: z.string(),
  brandId: z.preprocess(
    (val) => (typeof val === 'string' ? val.split(',') : val),
    z.array(z.string()).optional(),
  ),
  requestStatus: z.preprocess(
    (val) => (typeof val === 'string' ? val.split(',') : val),
    z.array(EventRequestStatusTableEnum).optional(),
  ),
  eventScheduleStatus: z.preprocess(
    (val) => (typeof val === 'string' ? val.split(',') : val),
    z.array(EventRequestScheduledStatusEnum).optional(),
  ),
  type: z.preprocess(
    (val) => (typeof val === 'string' ? val.split(',') : val),
    z.array(EventTypeEnum).optional(),
  ),
  scheduleType: z.preprocess(
    (val) => (typeof val === 'string' ? val.split(',') : val),
    z
      .array(z.enum(['one-time', 'recurring-daily', 'recurring-weekly', 'recurring-monthly']))
      .optional(),
  ),
  limit: z.preprocess((val) => (val ? Number(val) : undefined), z.number().optional()).default(10),
  offset: z
    .preprocess((val) => (val ? Number(val) : undefined), z.number().nonnegative().optional())
    .default(0),
  orderBy: SortableEventFieldsEnum.optional(),
  order: z.enum(['asc', 'desc']).optional(),
  search: z.string().optional(),
  markets: z.preprocess(
    (val) => (typeof val === 'string' ? val.split(',') : val),
    z.array(z.enum(AccountMarkets as [string, ...string[]])).optional(),
  ),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  timeZone: z.string().optional(),
});

export type ListEventsQueryParams = z.infer<typeof listEventsQueryParamsSchema>;

export const listEventsBodySchema = z.object({
  locationIds: z.array(z.string()).optional(),
});
export type ListEventsRequestBody = z.infer<typeof listEventsBodySchema>;

export const upsertEventProductSelectorFieldsBodySchema = z.object({
  posBrandIds: z.array(z.string().min(1)).optional(),
  posCategoryIds: z.array(z.string().min(1)).optional(),
  posProductIds: z.array(z.string().min(1)).optional(),
  productFilters: sparkProductFiltersSchema.optional(),
  retailerFilters: retailerSparkFiltersSchema.partial().optional().or(z.object({}).optional()),
  vendorFilters: vendorSparkFiltersSchema.partial().optional().or(z.object({}).optional()),
});
export type UpsertEventProductSelectorFieldsRequestBody = z.infer<
  typeof upsertEventProductSelectorFieldsBodySchema
>;

export const upsertEventBodySchema = z
  .object({
    _id: z.string().optional(),
    type: EventTypeEnum.optional(), // required if _id is not provided
    startTime: z.string().optional(), // required if _id is not provided
    endTime: z.string().optional(), // required if _id is not provided
    vendorAccountId: z.string().optional(),
    retailerAccountId: z.string().optional(), // required if _id is not provided
    brandId: z.string().optional(),
    locationIds: z.array(z.string()).optional(), // required if _id is not provided
    name: z.string().optional(), // required if _id is not provided
    description: z.string().optional(),
    requestNote: z.string().optional(),
    clonedFromId: z.string().optional(),
    retailerHostType: EventRetailerHostTypeEnum.optional(),
  })
  .refine(
    (data) => {
      if (data.retailerHostType === 'partner') {
        return !!data.vendorAccountId && !!data.brandId;
      }
      return true;
    },
    {
      message: 'Partner events must include both vendorAccountId and brandId',
      path: ['retailerHostType'],
    },
  );

export const declineEventBodySchema = z.object({
  reason: z.string().optional(),
  message: z.string().optional(),
});
export type DeclineEventRequestBody = z.infer<typeof declineEventBodySchema>;

// TODO: https://linear.app/sparkplug/issue/X-1483/secure-events-routes
// export const upsertRetailEventBodySchema = z.object({
//   _id: z.string().optional(),
//   type: EventTypeEnum,
//   startTime: z.string().min(1),
//   endTime: z.string().min(1),
//   locationIds: z.array(z.string().min(1)).min(1),
//   retailerAccountId: z.string().min(1),
//   name: z.string().min(1).max(32),
//   description: z.string().max(120),
// });
// export type UpsertRetailEventRequestBody = z.infer<typeof upsertRetailEventBodySchema>;

// TODO: https://linear.app/sparkplug/issue/X-1483/secure-events-routes
// export const upsertVendorEventBodySchema = z.object({
//   _id: z.string().optional(),
//   type: EventTypeEnum,
//   startTime: z.string().min(1),
//   endTime: z.string().min(1),
//   vendorAccountId: z.string().min(1),
//   retailerAccountId: z.string().min(1),
//   brandId: z.string().min(1),
//   locationIds: z.array(z.string().min(1)).min(1),
//   name: z.string().min(1).max(32),
//   description: z.string().max(120),
//   requestNote: z.string().max(180).optional(),
// });
// export type UpsertVendorEventRequestBody = z.infer<typeof upsertVendorEventBodySchema>;

export type UpsertEventRequestBody = z.infer<typeof upsertEventBodySchema>;

export const getEventCountQueryParamsSchema = z.object({
  accountId: z.string(),
  variant: EventCountVariantEnum,
});

export type GetEventCountQueryParams = z.infer<typeof getEventCountQueryParamsSchema>;
